import React, { useEffect, useRef, useState } from 'react';
import LayoutContainer from '../../components/Containers/LayoutContainer';
import CommonTable, { TableColumn } from '../../components/Table/CommonTable';
import PageHeader from '../../components/PageHeader/PageHeader';
import { FaRegEdit } from 'react-icons/fa';
import ModalWrapper from '../../components/Modal/ModalWrapper';
import InputField from '../../components/InputField/InputField';
import CustomButton from '../../components/CustomButton/CustomButton';
import APIList from '../../utils/constants/APIList';
import setInputValues from '../../utils/helpers/setInputValue';
import { postDetails } from '../../services/http/http.service';
import { ModalRef } from '../../utils/constants/Interfaces.constants';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store/storeRequests';
import { hideLoader, showLoader } from '../../store/Loader/loader.slice';
import { hideToast, showToast } from '../../store/Toast/toast.slice';

interface Folder {
    force_update: number;
    id: number;
    platform: string;
    created: Date;
    status: any;
}

const AppVersion: React.FunctionComponent = () => {
    const [folders, setFolders] = useState<Folder[]>([]);
    const [folderName, setFolderName] = useState('');
    const [selectedValue, setSelectedValue] = useState('');
    const cmpMounted = useRef(false);
    const navigate = useNavigate();

    const dispatch = useAppDispatch();
    const modalRef = useRef<ModalRef>(null);
   
    useEffect(() => {
        if (cmpMounted.current) return;
        cmpMounted.current = true;
        fetchGetAll();
    }, []);

    const capitalizeFirstLetter = (string: string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    const fetchGetAll = async () => {
        try {
            dispatch(showLoader('Loading data...'));
            const response = await postDetails(APIList.App_version_getall, {});
            const transformedData = response.data.map((item: any) => ({
                ...item,
                platform: item.platform === "android" ? capitalizeFirstLetter(item.platform) : item.platform.toUpperCase(),
                status: item.force_update,
            }));
            setFolders(transformedData || []);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            dispatch(hideLoader());
        }
    };

    const editFolder = async (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        
        if (!selectedValue) {
            // alert('Please select a platform.');
            dispatch(showToast(`Please select a platform.`));
        setTimeout(() => dispatch(hideToast()), 3000);
            return;
        }
    
        try {
            dispatch(showLoader('Loading data...'));

            // const appVersionNumber = (folderName);
            // if (isNaN(appVersionNumber)) {
            //     console.error('App_version must be a valid number');
            //     return;
            // }
            const payload = {
                id: folders.filter((folder: any) => folder.platform.toLowerCase() === selectedValue.toLowerCase())[0].id,  // Android: id=1, iOS: id=2
                platform: selectedValue,
                App_version: folderName,
            };

            const response = await postDetails(APIList.add_version, payload);
            // console.log(payload)
            closeModal();
            // navigate(0);
            fetchGetAll()
        } catch (error) {
            console.error('Error sending data:', error);
        } finally {
            dispatch(hideLoader());
        }
    };

    const toggleAppStatus = async (item: Folder) => {
        try {
            dispatch(showLoader('Updating status...'));
           const response = await postDetails(APIList.app_version_status, {
                id: item.id,
                force_update: item.force_update === 0 ? 1 : 0,
            });
            // console.log("STATUS TOGGLE RESPONSE",response)
           
            if(response.statusCode === 200){
                dispatch(showToast(`Update status toggled successfully.`));
                setTimeout(() => dispatch(hideToast()), 3000);
                fetchGetAll()
            } else{
                dispatch(showToast(`Unable to toggle status.`));
                setTimeout(() => dispatch(hideToast()), 3000);
            }

        } catch (error) {
            console.log(error);
        } finally {
            dispatch(hideLoader());
        }
    };

    const openModal = ()=>{
        resetForm()
        openEditFolderModal()
    }

    const resetForm = ()=>{
      setFolderName("")
      setSelectedValue("")
    }

    const openEditFolderModal = () => {
        if (modalRef.current) {
            modalRef.current.toggleModalWrapper(true);
        }
    };

    const closeModal = () => {
        if (modalRef.current) {
            modalRef.current.toggleModalWrapper(false);
        }
    };

    const handleChange = (event: any) => {
        setSelectedValue(event.target.value);
    };

    const columns: TableColumn[] = [
        { key: 'platform', label: 'Platform', sortable: false },
        { key: 'App_version', label: 'Version', sortable: false },
        { key: 'status', label: 'Force Update', dataType: 'toggle' },
    ];

    const buttons = [
        {
            label: 'Edit App Version',
            theme: 'warning',
            icon: <FaRegEdit />,
            onClick: openModal,
        },
    ];

    return (
        <LayoutContainer title="App Version Management">
            <ModalWrapper ref={modalRef} title="Edit App Version">
            <div className='mb-3'>
                <label htmlFor="" className='font-12 weight-500'>App Version Platform<span className='text-danger'>*</span></label>
            </div>
            <form onSubmit={editFolder}>
            <div className="form-check font-14">
                <input
                    className="form-check-input"
                    type="radio"
                    name="platform"
                    id="android"
                    value="android"
                    checked={selectedValue === 'android'}
                    onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="android">
                    Android
                </label>
            </div>
            <div className="form-check font-14">
                <input
                    className="form-check-input"
                    type="radio"
                    name="platform"
                    id="ios"
                    value="ios"
                    checked={selectedValue === 'ios'}
                    onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="ios">
                    IOS
                </label>
            </div>
                <div className='mt-4 mb-5 height-50px'>
                    <label htmlFor="app_version" className='font-12 weight-500 mb-1'>App Version <span className='text-danger'>*</span></label>
                    <InputField
                        type="text"
                        name="app_version"
                        // label="App Version"
                        required={true}
                        placeholder="Enter Version"
                        id="app_version"
                        minLength={1}
                        maxLength={10}
                        value={folderName}
                        // className="mb-4"
                        onChangeInput={(ev) => {
                            const value = ev.target.value;
                            if(/^[0-9]*([.][0-9]*)*$/.test(value)){
                            setInputValues(ev, folderName, setFolderName);
                            }
                        }}
                    />
                </div>
                <div className="mb-2">
                    <CustomButton theme="yellow" type="submit" size="md">
                        Update
                    </CustomButton>
                </div>
                </form>
            </ModalWrapper>
            <div className="page-header d-flex align-items-center justify-content-left dashboard-container h-100">
                <h2>
                    <i className="page-header-icon fa fa-home"></i>
                    App Version
                </h2>
            </div>
            <div className='mx-3'>
                <PageHeader buttons={buttons} />
            </div>
            {/* <CommonTable<Folder>
                data={folders}
                columns={columns}
                onToggle={toggleAppStatus}
                rowsPerPage={3}
                 actions={{
                        view: false,
                        edit: false,
                        delete: false,
                        checkbox: false,
                        status: false,
                        showModal: false,
                    }} */}
                    <CommonTable<Folder>
                    data={folders}
                    columns={columns}
                    // onEdit={editItem}
                    // onDelete={deleteItemById}
                    // onStatusToggle={toggleAppStatus}
                    onToggle={toggleAppStatus}
                    rowsPerPage={2}
                    pagination={false}
                    
                    actions={{
                        view: false,
                        edit: false,
                        delete: false,
                        checkbox: false,
                        status: false,
                        showModal: false,
                    }}
                />


            
        </LayoutContainer>
    );
};

export default AppVersion;
